import {
  FTUE_URL_PARAM,
  FTUE_V2_FLAG,
  ftueUrlOverride,
  ftueV2ArmFromFlag,
} from '@flashka/shared/ftue/flag';

import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { useState } from 'react';

const OVERRIDE_KEY = 'flashka.ftue-v2.override';

/**
 * `?ftue=v2` / `?ftue=off` is remembered on this browser (sign-up redirects
 * drop the query string), so a preview link with the param keeps the flow on
 * through auth. Read once per mount; the param itself wins over the memory.
 */
function readOverride(): boolean | null {
  try {
    const fromUrl = ftueUrlOverride(window.location.search);
    if (fromUrl !== null) {
      window.localStorage.setItem(OVERRIDE_KEY, fromUrl ? '1' : '0');
      const url = new URL(window.location.href);
      url.searchParams.delete(FTUE_URL_PARAM);
      window.history.replaceState(window.history.state, '', url);
      return fromUrl;
    }
    const stored = window.localStorage.getItem(OVERRIDE_KEY);
    return stored === '1' ? true : stored === '0' ? false : null;
  } catch {
    return null;
  }
}

/**
 * Called once from the entry module, BEFORE the router runs: the sign-in and
 * onboarding redirects drop the query string, so a `?ftue=v2` link has to be
 * remembered while it is still in the address bar.
 */
export function captureFtueOverrideFromUrl(): void {
  readOverride();
}

/**
 * FTUE v2 gate: the URL/browser override, else the `test` arm of the PostHog
 * experiment `ftue-v2` (by variant, not truthiness: see `ftueV2ArmFromFlag`).
 */
export function useFtueV2Enabled(): boolean {
  const flag = ftueV2ArmFromFlag(useFeatureFlagVariantKey(FTUE_V2_FLAG)) === true;
  const [override] = useState(readOverride);
  return override ?? flag;
}
